<template>
	<layout-page title="Welcome to the new auction on cropspot!">
		<crop-filter
			v-model="filter"
			v-if="showFilter"
			v-on:close="closeFilter()"
			:show-city="false"
			:show-city-options="true"
			:all-city-options="allCityOptions"
		/>

		<div class="flex-grow flex flex-col">
			<!-- header -->
			<div
				class="bg-green px-4 sm:px-6 py-4 md:rounded-tl-2xl md:rounded-tl-2xl xl:rounded-t-2xl flex justify-start items-center"
			>
				<h1 class="text-2xl md:text-4xl font-semibold text-white">
					{{ __("Welcome to the new auction on cropspot!") }}
				</h1>
			</div>

			<!-- filter bar -->
			<div
				class="px-4 sm:px-6 lg:px-6 py-4 bg-gray-700 flex-wrap flex justify-between items-center md:space-x-6"
				v-if="auctionIsActive && !auctionResultsIsActive"
			>
				<button
					class="relative inline-flex items-center px-2 lg:pr-4 py-1.5 font-medium text-white bg-gray-500 hover:bg-gray-400 rounded-lg flex items-center focus:ring-2 ring-gray-400"
					v-on:click="showFilter = !showFilter"
				>
					<ui-icon
						class="mr-2 flex-shrink-0 h-5 w-5"
						name="filter"
						stroke-width="2"
					/>
					{{ __("Filter") }}
					<span
						class="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-or-50 transform translate-x-1/4 -translate-y-1/2 bg-orange rounded-full"
						v-if="activeFilterCount > 0"
						>{{ activeFilterCount }}</span
					>
				</button>

				<p
					class="text-gray-100 font-medium hidden lg:block"
					v-if="offers.length"
				>
					<span class="hidden xl:inline">{{ __("Showing results") }}</span>
					{{
						__("messages.results_of_total", {
							args: {
								results: filteredOffers.length,
								total: offers.length,
							},
						})
					}}
					<span class="">{{ __("Sell Offers") }}</span>
				</p>
			</div>

			<auction-info-page :auction="currentAuction" v-if="!auctionIsActive" />

			<auction-list
				:offers="filteredOffers"
				:auction="currentAuction"
				:activeFutures="activeFutures"
				:auctionIndexState="auctionIndexState"
				:showList="showList"
				:results="results"
				v-else-if="auctionIsActive"
			/>
		</div>
	</layout-page>
</template>

<script>
import Vue from "vue";
import { uniq } from "lodash";

export default {
	props: {
		offers: {
			type: Array,
			required: true,
		},
		auction: {
			type: Object,
			required: false,
			default: null,
		},
		upcomingAuction: {
			type: Object,
			required: false,
			default: null,
		},
		activeFutures: {
			type: Array,
			default: () => [],
		},
		auctionIndexState: {
			type: String,
			required: false,
			default: "ANNOUNCE",
		},
		showList: {
			type: Boolean,
			required: false,
			default: false,
		},
		results: {
			type: Object,
			default: () => {},
		},
	},
	data() {
		return {
			filter: Vue.observable({
				productGrades: [],
				productYears: [],
				type: {
					BUY: true,
					SELL: true,
				},
				dateType: [],
				monthType: {
					month_from: null,
					month_to: null,
				},
				locationModes: [],
				locationAddressCity: [],
				locationAddressCityOptions: [],
			}),
			showFilter: false,
			currentAuction:
				this.auction !== null ? this.auction : this.upcomingAuction,
		};
	},
	methods: {
		closeFilter() {
			this.showFilter = false;
		},
		intervalCheck(month_from, month_to) {
			let selectedMonthFrom = this.filter.monthType.month_from,
				selectedMonthTo = this.filter.monthType.month_to,
				leftCheck =
					selectedMonthFrom == null ||
					month_from == null ||
					month_from >= selectedMonthFrom,
				rightCheck = selectedMonthTo == null || selectedMonthTo >= month_to;
			return leftCheck && rightCheck;
		},
	},
	computed: {
		auctionIsActive() {
			if (this.auction !== null && this.auctionIndexState !== "ANNOUNCE") {
				return true;
			}
			return false;
		},
		auctionResultsIsActive() {
			if (this.auction !== null && this.auctionIndexState == "RESULTS") {
				return true;
			}
			return false;
		},
		activeFilterCount() {
			return Object.values(this.filter).flat().length - 2;
		},
		filteredOffers() {
			let //mine = this.filterOptions['mine'],
				//selectedHubs = this.getSelectedIds(this.filterOptions['location__hub']),
				selectedTypes = this.filter.type,
				selectedGrades = this.filter.productGrades,
				selectedHarvests = this.filter.productYears,
				selectedDates = this.filter.dateType,
				selectedLocationModes = this.filter.locationModes,
				selectedLocationCityOptions = this.filter.locationAddressCityOptions;
			return (
				this.offers
					//.filter(offer => mine ? offer.mine : true)
					.filter(
						(offer) =>
							selectedLocationModes.length == 0 ||
							selectedLocationModes.includes(offer.location.mode)
					)
					.filter(
						(offer) =>
							selectedLocationCityOptions.length == 0 ||
							selectedLocationCityOptions.includes(offer.location.address?.city)
					)
					.filter(
						(offer) =>
							selectedGrades.length == 0 ||
							(!offer.product.grade.includes("WHEAT_B") &&
								selectedGrades.includes(offer.product.grade)) ||
							(offer.product.grade.includes("WHEAT_B") &&
								selectedGrades.includes("WHEAT_B"))
					)
					.filter(
						(offer) =>
							selectedHarvests.length == 0 ||
							selectedHarvests.includes(offer.product.harvest)
					)
					.filter(
						(offer) =>
							selectedDates.length == 0 ||
							(selectedDates.includes("AT_HARVEST") &&
								offer.date.type == "AT_HARVEST") ||
							(selectedDates.includes("MONTH") &&
								offer.date.type != "AT_HARVEST" &&
								this.intervalCheck(offer.date.month_from, offer.date.month_to))
					)
					.filter((offer) => selectedTypes[offer.trade] == true)
			);
		},
		allCityOptions() {
			let allCities = this.offers
				.map((o) => o.location.address.city ?? null)
				.filter((o) => o != null);
			return uniq(allCities);
		},
	},
};
</script>
